import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { en } from "./en";
import { ru } from "./ru";

export type Lang = "en" | "ru";

export const LANGUAGES: { code: Lang; label: string; short: string }[] = [
  { code: "en", label: "English", short: "EN" },
  { code: "ru", label: "Русский", short: "RU" },
];

const STORAGE_KEY = "ces.lang";

// All dictionaries must have the same key shape — en is the source of truth.
export type Dict = typeof en;
export type TranslationKey = NestedKeyOf<Dict>;

type NestedKeyOf<T> = T extends string
  ? never
  : {
      [K in keyof T & string]: T[K] extends string
        ? K
        : `${K}.${NestedKeyOf<T[K]>}`;
    }[keyof T & string];

const dictionaries: Record<Lang, Dict> = { en, ru };

function pickFromDict(dict: Dict, key: string): string | undefined {
  const path = key.split(".");
  let cur: unknown = dict;
  for (const segment of path) {
    if (cur && typeof cur === "object" && segment in (cur as Record<string, unknown>)) {
      cur = (cur as Record<string, unknown>)[segment];
    } else {
      return undefined;
    }
  }
  return typeof cur === "string" ? cur : undefined;
}

function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, name) => {
    return name in vars ? String(vars[name]) : match;
  });
}

function langFromHost(): Lang | null {
  if (typeof window === "undefined") return null;
  const host = window.location.hostname.toLowerCase();
  if (host.endsWith("backdoor.tech")) return "en";
  return null;
}

// Host-locked locale: app.backdoor.tech only ever serves EN. The switcher is
// hidden, localStorage is ignored, and setLang() is a no-op on that host.
// Dev / preview / localhost hosts still honour localStorage so contributors
// can flip languages while testing.
export function isLangLocked(): boolean {
  return langFromHost() !== null;
}

function readLang(): Lang {
  // Host-enforced locale wins over everything else — see isLangLocked().
  const fromHost = langFromHost();
  if (fromHost) return fromHost;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "en" || stored === "ru") return stored;
  } catch {
    /* ignore */
  }
  const nav = typeof navigator !== "undefined" ? navigator.language : "";
  return nav?.toLowerCase().startsWith("ru") ? "ru" : "en";
}

type Ctx = {
  lang: Lang;
  setLang: (next: Lang) => void;
  t: (key: TranslationKey, vars?: Record<string, string | number>) => string;
};

const I18nContext = createContext<Ctx | null>(null);

export function LangProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => readLang());

  useEffect(() => {
    document.documentElement.lang = lang;
    // Brand + tagline in the browser tab, per locale (see meta.title in en/ru).
    document.title = dictionaries[lang].meta.title;
  }, [lang]);

  const setLang = useCallback((next: Lang) => {
    // No-op on host-locked deployments — the UI hides the switcher,
    // but defend against direct callers (e.g. URL ?lang= handlers added
    // later) trying to override the enforced locale.
    if (isLangLocked()) return;
    setLangState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }, []);

  const t = useCallback<Ctx["t"]>(
    (key, vars) => {
      const dict = dictionaries[lang];
      const direct = pickFromDict(dict, key);
      if (direct !== undefined) return interpolate(direct, vars);
      // Fall back to English so a missing Russian key never blanks the UI.
      const fallback = pickFromDict(dictionaries.en, key);
      if (fallback !== undefined) return interpolate(fallback, vars);
      // Surface the missing key during development.
      return key;
    },
    [lang],
  );

  const value = useMemo<Ctx>(() => ({ lang, setLang, t }), [lang, setLang, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/** Non-hook translation for modules outside the React tree (e.g. src/api.ts).
 * Resolves the active lang the same way LangProvider does (host lock →
 * localStorage → navigator), with the same English fallback chain. */
export function translate(
  key: TranslationKey,
  vars?: Record<string, string | number>,
): string {
  const direct = pickFromDict(dictionaries[readLang()], key);
  if (direct !== undefined) return interpolate(direct, vars);
  const fallback = pickFromDict(dictionaries.en, key);
  if (fallback !== undefined) return interpolate(fallback, vars);
  return key;
}

export function useI18n(): Ctx {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside <LangProvider>");
  return ctx;
}

export function useT() {
  return useI18n().t;
}

/** Translate a backend node/mission status string (`pending`/`running`/...)
 * to the user's locale. Unknown statuses fall through unchanged so we don't
 * silently hide new values the backend introduces. */
export function translateMissionStatus(
  status: string | null | undefined,
  t: (key: TranslationKey, vars?: Record<string, string | number>) => string,
): string {
  switch ((status ?? "").trim().toLowerCase()) {
    case "pending":
      return t("chat.missionStatusPending");
    case "running":
      return t("chat.missionStatusRunning");
    case "completed":
    case "succeeded":
    case "success":
      return t("chat.missionStatusCompleted");
    case "failed":
    case "error":
      return t("chat.missionStatusFailed");
    case "cancelled":
    case "canceled":
      return t("chat.missionStatusCancelled");
    case "skipped":
      return t("chat.missionStatusSkipped");
    default:
      return status ?? "";
  }
}
